import { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { useNetworkStatus } from "../../common/hooks/useNetworkStatus";
import { fetchPatientRecordsAsync, setDate, updateTranscriptSectionStatus } from "../redux/patientTableRecordsSlice";
import { fetchAllDocuments } from "@redux/documentSlice";
import store from "@/app/store";
import { getCreatorSectionNames } from "@/common/lib/supabaseClient";
import { useAccountContext } from "./AccountProvider";
import { UAParser } from "ua-parser-js";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import AudioDb, { STALE_OWNER_MS, TranscriptSectionIndexDB } from "@/common/classes/audioDb";
import { RecordingState, TranscriptSectionStatus } from "@/common/utils/types";
import { shouldRecoverIncompleteUploads, findLiveRecordingTabs } from "@/common/lib/recorderPersistence";
import { onSessionEnded } from "@/common/lib/crossTabSession";
import { useIsPulse } from "@/state/hooks/useIsPulse";
import { useImpersonation } from "./ImpersonationProvider";
import { useAllDoctors } from "@/state/hooks/useDoctors";
import { useImpersonationAwareUser } from "@/common/hooks/useImpersonationAwareUser";
import { switchDoctor } from "@/state/redux/doctorSlice";

const supportedBrowsers = {
    Chrome: 100, // Unknown here.
    Edge: 102, // Unknown here: Released May 2022
    Safari: 14, // Known constraint with Media Recorder
    Firefox: 100, //Unknown here: Released May 2022
};
type BrowserName = keyof typeof supportedBrowsers;

// EN-1609: how often incomplete-upload recovery re-runs while signed in (see the recovery poll effect).
const RECOVERY_POLL_MS = 5 * 60_000;
// EN-1609: Web Lock held for a recovery pass, so only one tab recovers at a time.
const RECOVERY_LOCK = "happydoc-upload-recovery";
// EN-1609: every tab hears a session-ended announcement; spread their follow-up passes out.
const SESSION_ENDED_JITTER_MS = 30_000;

export const DataContextApi = createContext<{
    localAudioDb: React.MutableRefObject<any>;
    selectedDate: {
        startDate: Date | null;
        endDate: Date | null;
    };
    setSelectedDate: React.Dispatch<
        React.SetStateAction<{
            startDate: Date | null;
            endDate: Date | null;
        }>
    >;
    isLoadingData: boolean;
    setIsLoadingData: React.Dispatch<React.SetStateAction<boolean>>;
    refreshPatientRecords: () => Promise<boolean>;
    setSectionsAndFieldsToBold: React.Dispatch<React.SetStateAction<Set<string>>>;
    sectionsAndFieldsToBold: Set<string>;
    getIncompleteUploads: () => Promise<TranscriptSectionIndexDB[]>;
    incompleteUploads: TranscriptSectionIndexDB[];
    setIncompleteUploads: React.Dispatch<React.SetStateAction<TranscriptSectionIndexDB[]>>;
}>({
    localAudioDb: { current: null },
    selectedDate: { startDate: new Date(), endDate: new Date() },
    setSelectedDate: () => {},
    isLoadingData: false,
    setIsLoadingData: () => {},
    refreshPatientRecords: async () => true,
    setSectionsAndFieldsToBold: () => {},
    sectionsAndFieldsToBold: new Set<string>(),
    getIncompleteUploads: async () => [],
    incompleteUploads: [],
    setIncompleteUploads: () => {},
});

/**
 * DataContextProvider is a component that provides the DataContextApi values to its child components.
 * It manages states such as localAudioDb, selectedDate, and isLoadingData, and handles the logic
 * for updating patient records and fetching data based on session and selected doctor.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 */
export const DataContextProvider = ({ children }: { children: React.ReactNode }) => {
    const session = useSession();
    const { impersonationSession, isImpersonating } = useImpersonation();
    const isOnline = useNetworkStatus();

    const dispatch = useAppDispatch();
    const { dataLoaded, accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;
    const isPulse = useIsPulse();

    const { status } = useAppSelector((state) => state.documents);
    const selectedDoctors = useAppSelector((state) => state.doctors.selectedDoctors);
    const selectedDoctor = useAppSelector((state) => state.doctors.selectedDoctor);
    const [selectedDate, setSelectedDate] = useState({
        startDate: new Date(),
        endDate: new Date(),
    });
    const selectedDateRef = useRef(selectedDate);
    selectedDateRef.current = selectedDate;
    const clinicAccountIdRef = useRef(clinicAccountId);
    clinicAccountIdRef.current = clinicAccountId;
    const [sectionsAndFieldsToBold, setSectionsAndFieldsToBold] = useState(new Set<string>());
    const [isLoadingData, setIsLoadingData] = useState(dataLoaded ? false : true);
    const [incompleteUploads, setIncompleteUploads] = useState<TranscriptSectionIndexDB[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const doctorsInitializedRef = useRef(false);
    const lastDoctorInitKeyRef = useRef<string | null>(null);

    // Get doctors from React Query for initialization
    const { userId } = useImpersonationAwareUser();
    const { query: doctorsQuery, doctors: doctorsData } = useAllDoctors();

    // Reset doctor-selection init when effective user or resolved clinic changes (impersonation / switch clinic)
    useEffect(() => {
        const key = `${userId ?? ""}::${clinicAccountId ?? ""}`;
        if (key !== lastDoctorInitKeyRef.current) {
            doctorsInitializedRef.current = false;
            lastDoctorInitKeyRef.current = key;
        }
    }, [userId, clinicAccountId]);

    useEffect(() => {
        refreshPatientRecords();
    }, [selectedDoctors, selectedDate]);

    /**
     * Initialize doctor selection and document selection on app load.
     * This restores the initialization logic that was lost when migrating from Redux thunk to React Query.
     *
     * 1. Auto-selects the first doctor when there's only one doctor and no doctor is currently selected
     * 2. Initializes selectedDocuments from the selected doctor's doctor_document_types
     *
     * This only runs once when doctors are first loaded to avoid interfering with manual doctor switching.
     */
    useEffect(() => {
        // Only proceed if:
        // - Data is loaded
        // - Doctors query has data (a failed refetch keeps the last good roster)
        // - We haven't already initialized
        if (!dataLoaded || doctorsInitializedRef.current) return;

        // If the doctors query failed (after React Query's retries) with no roster cached, surface
        // the error and mark initialization as done so the app doesn't wait indefinitely with no
        // doctors. A failed refetch keeps the last good roster in data (EN-1419), so initialize
        // from that instead.
        if (doctorsQuery.isError && !doctorsQuery.data) {
            Sentry.captureException(new Error("Doctor initialization skipped: doctors query failed permanently"));
            doctorsInitializedRef.current = true;
            return;
        }

        if (!doctorsQuery.data) {
            return;
        }

        const doctors = doctorsData;
        const activeDoctors = doctors.filter((d) => !d.is_archived && (isPulse ? !d.sync_excluded : true));

        // If there are no active doctors, nothing to initialize
        if (activeDoctors.length === 0) {
            doctorsInitializedRef.current = true;
            return;
        }

        // Auto-select the first doctor if there's only one and no doctor is currently selected
        if (activeDoctors.length === 1 && !selectedDoctor) {
            const firstDoctor = activeDoctors[0];
            dispatch(switchDoctor(firstDoctor) as any);
            doctorsInitializedRef.current = true;
            return;
        }

        // If a doctor is already selected (from localStorage or previous selection),
        // initialize selectedDocuments from that doctor's doctor_document_types
        // This ensures document selection is synced with server changes on app load
        if (selectedDoctor?.id) {
            // Get the fresh doctor data from React Query cache to ensure we have the latest doctor_document_types
            const freshDoctor = doctors.find((d) => d.id === selectedDoctor.id);
            if (freshDoctor) {
                // Re-dispatch switchDoctor with fresh data to sync selectedDocuments with the selected doctor's doctor_document_types
                // This handles cases where server-side changes to doctor_document_types haven't been reflected in Redux state
                dispatch(switchDoctor(freshDoctor) as any);
            }
        }

        doctorsInitializedRef.current = true;
        // doctorsQuery.data is excluded: doctorsData is derived from it and re-triggers the effect
        // when the roster changes. The guards above read data only for presence: data first appears with
        // a successful fetch (isSuccess flips, a dep), and a failed refetch that keeps it leaves
        // data unchanged, so there is nothing new to re-run for.
    }, [dataLoaded, doctorsQuery.isSuccess, doctorsQuery.isError, doctorsData, selectedDoctor, dispatch]);

    /**
     * Get all incomplete uploads
     * @returns {Promise<TranscriptSectionIndexDB[]>}
     * */
    const getIncompleteUploads = async (): Promise<TranscriptSectionIndexDB[]> => {
        const localAudioDb = AudioDb.getInstance();

        const incompleteUploads = await localAudioDb.getRecordings({ uploadComplete: false });

        if (incompleteUploads) {
            setIncompleteUploads(incompleteUploads);
            incompleteUploads.forEach((upload) => {
                dispatch(
                    updateTranscriptSectionStatus({
                        id: upload.patientRecordId,
                        sectionId: upload.transcriptSectionId,
                        status: TranscriptSectionStatus.FailedIncompleteUpload,
                    }),
                );
            });
        }

        return incompleteUploads;
    };

    // Latest recording/upload state, read at recovery time without re-creating the callback.
    const globalRecordingState = useAppSelector((state) => state.globalState.globalRecordingState);
    const isUploadingGlobal = useAppSelector((state) => state.globalState.isUploadingGlobal);
    const recoveryGateRef = useRef({ isRecording: false, isUploading: false });
    recoveryGateRef.current = {
        isRecording:
            globalRecordingState === RecordingState.recording || globalRecordingState === RecordingState.paused,
        isUploading: isUploadingGlobal,
    };
    const recoveryInFlightRef = useRef(false);

    // EN-836: re-attempt any recordings persisted to IndexedDB that never finished uploading —
    // including recordings interrupted mid-capture by a reload/crash (useAudioRecorder writes
    // periodic snapshots as incomplete uploads). Previously AudioDb.retryIncompleteUploads() had
    // no callers, so these recordings were stranded and lost.
    const recoverIncompleteUploads = useCallback(async () => {
        // Never recover while a recording or upload is active: retryIncompleteUploads uploads every
        // incomplete row, which mid-recording would ship the in-progress snapshot (a partial file),
        // transcribe it, then upload the full file again at stop — a truncated transcription plus a
        // duplicate section/doc-gen (EN-652). Startup is unaffected (nothing records at boot); this
        // guard is what makes the network-restore trigger safe.
        if (!shouldRecoverIncompleteUploads(recoveryGateRef.current)) return;
        // Belt-and-suspenders with AudioDb's own in-flight lock: don't let the startup and
        // network-restore effects overlap on the same client.
        if (recoveryInFlightRef.current) return;
        recoveryInFlightRef.current = true;
        try {
            // Cross-tab guard: another tab may be recording (this tab's redux can't see it), and
            // mid-recording snapshots live in shared IndexedDB. A live recording tab answers with its
            // nonce; a closed/crashed one can't. EN-1609: pass the live set down so AudioDb skips only
            // the rows live tabs own, instead of aborting the whole pass whenever any tab records.
            const pass = async () => {
                const liveOwners = await findLiveRecordingTabs();
                // PR review finding: recordings queued before EN-1216 added accountId to the persisted
                // schema read back as undefined and, unguarded, would hard-fail this retry forever.
                // Pass the live clinic account as a fallback -- only used when the recording itself
                // never captured one, so a genuinely captured (possibly different, EN-1171) account
                // always wins inside retryIncompleteUploads.
                await AudioDb.getInstance().retryIncompleteUploads(
                    undefined,
                    undefined,
                    true,
                    clinicAccountId,
                    liveOwners,
                );
            };
            // EN-1609: one tab's pass at a time across the browser, so idle tabs don't all upload the
            // same recovered row (AudioDb's per-row claim is the backstop where Web Locks is missing).
            // Another tab already running a pass covers this one, so skip rather than queue.
            if (typeof navigator !== "undefined" && navigator.locks) {
                await navigator.locks.request(RECOVERY_LOCK, { ifAvailable: true }, async (lock) => {
                    if (lock) await pass();
                });
            } else {
                await pass();
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { errorCategory: "recording", errorId: "recording.recover_incomplete_uploads_failed" },
            });
        } finally {
            recoveryInFlightRef.current = false;
            try {
                await getIncompleteUploads();
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { errorCategory: "recording", errorId: "recording.get_incomplete_uploads_failed" },
                });
            }
        }
        // getIncompleteUploads is recreated each render but is safe to omit here
    }, [clinicAccountId]);

    // Run once per user on startup. Keyed on the user id (not a bare boolean) so a SPA
    // logout/login as a different user on the same provider instance re-runs recovery for them.
    //
    // PR review finding: also gated on `dataLoaded`, not just `userId`. session.user.id typically
    // resolves (from cached storage) well before accountData's own network fetch settles, so this
    // effect used to fire -- and lock itself out via the ref -- with clinicAccountId still
    // undefined. recoverIncompleteUploads' fallbackAccountId then closed over that stale
    // undefined, and because the ref already marked this userId done, the LATER re-created
    // closure (once clinicAccountId actually resolved) never got a chance to run: pre-EN-1216
    // rows would hit the refuse-without-accountId guard on every startup, forever. `dataLoaded`
    // flips true once the account fetch settles either way (success or failure -- see
    // AccountProvider's own comment on it), so waiting for it means the fallback this effect
    // captures is either the real clinicAccountId or a confirmed "this user has none", never a
    // request that just hasn't finished yet.
    const startupRecoveredUserRef = useRef<string | null>(null);
    useEffect(() => {
        const userId = session?.user?.id;
        if (!userId || !dataLoaded || startupRecoveredUserRef.current === userId) return;
        startupRecoveredUserRef.current = userId;
        // Defensive: clear any in-flight guard left set by the previous user on a same-session
        // logout/login (this provider can persist across the switch). The flag always resets in
        // recoverIncompleteUploads' finally, so this only matters if a prior recovery is still
        // in-flight at the exact moment we switch — in which case the new user's startup pass
        // should still run rather than be skipped by a stale flag.
        recoveryInFlightRef.current = false;
        recoverIncompleteUploads();
    }, [session?.user?.id, dataLoaded, recoverIncompleteUploads]);

    // Retry again when the network is restored (offline -> online) — a connectivity drop is the
    // most common cause of an incomplete/stranded upload in the first place.
    //
    // PR review finding: also gated on `dataLoaded`, same reasoning as the startup effect above.
    // Coming back online can itself happen before accountData's fetch has settled (e.g. the app
    // booted offline), and without this gate that pass would run with clinicAccountId still
    // undefined -- pre-EN-1216 rows would hit the refuse-without-accountId guard. Worse, this
    // pass holds recoveryInFlightRef for its full duration (it's awaited inside
    // recoverIncompleteUploads), so if dataLoaded resolves WHILE it's still running, the startup
    // effect above would fire, immediately no-op against that still-set flag, but still mark
    // startupRecoveredUserRef done -- permanently losing the one attempt that would have had a
    // real fallback. In steady state (app already loaded, network drops and restores later)
    // dataLoaded is already true, so this changes nothing for the common case.
    const wasOnlineRef = useRef(isOnline);
    useEffect(() => {
        const cameBackOnline = isOnline && !wasOnlineRef.current;
        wasOnlineRef.current = isOnline;
        if (cameBackOnline && session?.user?.id && dataLoaded) {
            recoverIncompleteUploads();
        }
    }, [isOnline, session?.user?.id, dataLoaded, recoverIncompleteUploads]);

    // EN-1609: startup and `online` alone never revisit a tab closed mid-session, and in a clinic that
    // always has a tab open no startup comes. Poll, and also re-run once a closed tab's snapshot has
    // gone stale (STALE_OWNER_MS after it announced its session ended, plus up to 30s of jitter).
    // recoverIncompleteUploads' own gates (this tab recording/uploading, in-flight) still apply to
    // every pass.
    // Not while offline: every failed pass bumps each row's retryCount, and these passes count
    // toward RETRY_LIMIT, so polling through an outage would retire rows from automatic recovery.
    useEffect(() => {
        if (!session?.user?.id || !dataLoaded || !isOnline) return;
        const interval = setInterval(recoverIncompleteUploads, RECOVERY_POLL_MS);
        const pending = new Set<ReturnType<typeof setTimeout>>();
        const stopListening = onSessionEnded(() => {
            const timer = setTimeout(
                () => {
                    pending.delete(timer);
                    recoverIncompleteUploads();
                },
                STALE_OWNER_MS + Math.random() * SESSION_ENDED_JITTER_MS,
            );
            pending.add(timer);
        });
        return () => {
            clearInterval(interval);
            pending.forEach(clearTimeout);
            stopListening();
        };
    }, [isOnline, session?.user?.id, dataLoaded, recoverIncompleteUploads]);

    // Function to fetch data sequentially
    const fetchSequentially = async () => {
        // Abort any previous fetches
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create a new AbortController
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            // Scope visits/documents/creator fields to the resolved clinic account (multi-clinic users).
            if (session?.user?.id && dataLoaded && clinicAccountId) {
                setIsLoadingData(true);

                // Documents, patient records (+ incomplete uploads, a local IndexedDB read), and
                // creator field names are all scoped to clinicAccountId alone -- none consumes
                // another's result. Awaiting them one at a time here (each a network round trip)
                // was the main contributor to a ~10s initial load; run them concurrently instead.
                const tasks: Promise<unknown>[] = [];

                // Skip fetching documents on settings page to avoid conflicts with DocumentTypesPage
                if (status !== "loading" && !window.location.pathname.startsWith("/settings")) {
                    tasks.push(
                        dispatch(
                            fetchAllDocuments({
                                accountID: clinicAccountId,
                                signal: controller?.signal,
                            }) as any,
                        ),
                    );
                }

                // Doctors are now fetched via React Query (useDoctors hook) - no need to fetch here

                // EN-1312: no "already loading" guard. Skipping here after aborting the previous run
                // above could leave that aborted fetch as the final result. Superseded fetches are
                // discarded by the requestId gate in patientTableRecordsSlice instead.
                if (selectedDate?.startDate && selectedDate?.endDate) {
                    // Get selected doctor IDs from Redux state
                    const state = store.getState() as any;
                    const selectedDoctors = state.doctors.selectedDoctors || [];
                    const doctorIds = selectedDoctors.length > 0 ? selectedDoctors.map((d: any) => d.id) : null;
                    // getIncompleteUploads must run AFTER fetchPatientRecordsAsync settles, not
                    // alongside it: fetchPatientRecordsAsync.fulfilled wholesale-replaces
                    // tableRecords, while getIncompleteUploads patches an existing record in place
                    // (updateTranscriptSectionStatus). Running them concurrently lets the (fast,
                    // local IndexedDB) patch land before the (slower, network) replace and get
                    // silently wiped out.
                    //
                    // Code-review finding (2026-09-20): dispatch(...) isn't unwrap()'d, so this
                    // .then() runs whether fetchPatientRecordsAsync fulfilled or rejected -- same
                    // as the sequential `await dispatch(...); await getIncompleteUploads();` this
                    // replaced (pre-existing behavior, not introduced by parallelizing it).
                    // Deliberately not gated on success: incomplete-upload recovery is a local
                    // IndexedDB concern orthogonal to whether the remote patient-record fetch
                    // succeeded, and fetchPatientRecordsAsync.rejected never touches tableRecords
                    // (see patientTableRecordsSlice.ts), so a failed fetch leaves only the same
                    // stale-but-present records this patch would already be touching pre-fetch.
                    tasks.push(
                        dispatch(
                            fetchPatientRecordsAsync({
                                selectedDate,
                                doctorIds,
                                abortSignal: controller.signal,
                                userId: clinicAccountId,
                            }) as any,
                        ).then(() => getIncompleteUploads()),
                    );
                }

                tasks.push(
                    getCreatorSectionNames(controller?.signal, clinicAccountId).then((result) => {
                        if (result) {
                            setSectionsAndFieldsToBold(new Set(result));
                        }
                    }),
                );

                // allSettled, not all: these tasks are independent, so one failing shouldn't
                // short-circuit isLoadingData back to false while the others are still in flight
                // (Promise.all rejects on the first failure but does NOT cancel the rest, which
                // would then keep mutating state after the loading indicator already disappeared)
                // -- and every failure should be reported, not just whichever settles first.
                const results = await Promise.allSettled(tasks);
                results.forEach((result) => {
                    if (result.status === "rejected") {
                        Sentry.captureException(result.reason);
                    }
                });
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setIsLoadingData(false);

            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }

            controller.abort();
        }
    };

    useEffect(() => {
        if (dataLoaded) {
            fetchSequentially();
        }
    }, [session?.user?.id, dataLoaded, clinicAccountId, dispatch, isImpersonating, impersonationSession]);

    useEffect(() => {
        /**
         * Schedules an update to occur at the next midnight.
         *
         * This function calculates the time remaining until the next midnight and sets a timeout to update the selected date and dispatch a new date to the state at that time.
         * After the update, it recursively schedules the next update for the following midnight.
         *
         * @returns {NodeJS.Timeout} The timer ID for the scheduled update.
         */
        const scheduleMidnightUpdate = () => {
            const now = new Date();
            const nextMidnight = new Date(now);
            // set to exactly one second past midnight
            nextMidnight.setHours(24, 0, 1, 0);
            const msUntilMidnight = nextMidnight.getTime() - now.getTime();

            const timer = setTimeout(() => {
                const newDate = new Date();
                setSelectedDate({ startDate: newDate, endDate: newDate });
                dispatch(setDate(newDate.toISOString()));
                scheduleMidnightUpdate(); // schedule the next update
            }, msUntilMidnight);

            return timer;
        };

        const timerId = scheduleMidnightUpdate();
        return () => clearTimeout(timerId);
    }, []);

    // Effect to check the browser version on component mount and recommend an update if necessary
    useEffect(() => {
        try {
            const parser = new UAParser();
            const result = parser.getBrowser();
            const majorVersion = result?.major ? parseInt(result?.major, 10) : null;
            if (!majorVersion) return;

            // Using a type guard to ensure result.name is a key of supportedBrowsers
            if (typeof result?.name === "string" && result.name in supportedBrowsers) {
                const browserName = result.name as BrowserName; // Cast to BrowserName type
                if (majorVersion <= supportedBrowsers[browserName]) {
                    toast.error(
                        "Uh oh! This browser may not be entirely supported by HappyDoc. For best experience, we recommend upgrading to the latest version.",
                        {
                            autoClose: false,
                            position: "top-right",
                        },
                    );
                }
            }
        } catch (error) {
            Sentry.captureException(error);
        }
    }, []);

    // Function to refresh patient records manually. Returns whether this call's own fetch did not
    // reject (PR review, 2026-08-26; EN-1312: a superseded fetch that fulfills still returns true
    // although the requestId gate discarded its rows): Visit.tsx's periodic-poll effect needs its
    // OWN trigger's outcome specifically, not the slice's shared status (selectTableRecordStatus,
    // which also reflects manual doctor/date changes and SyncAppointmentsBtn) -- see that effect's
    // own comment for why conflating the two made its stale-data warning both over- and under-fire.
    const refreshPatientRecords = useCallback(async (): Promise<boolean> => {
        // Only proceed if user is logged in and data is loaded
        // Date and clinic are read through refs (EN-1312): SyncAppointmentsBtn and ImportModal call
        // a copy of this function captured before a long await, so closed-over values would fetch
        // the day and clinic that were showing when they started (in-app impersonation start
        // changes the clinic without a reload), not the ones showing now.
        const clinicAccountId = clinicAccountIdRef.current;
        if (!session?.user?.id || !dataLoaded || !clinicAccountId) {
            return true; // nothing attempted -- not a failure
        }
        // EN-1312: always fetch. This used to return early while a fetch was in flight, which
        // silently dropped any date or doctor change made during it (and during a PIMS sync, which
        // holds status "loading" for its whole run): nothing retried, so the list stayed on the old
        // day under the new header. Overlapping fetches are safe because only the most recently
        // issued one may write the table (requestId gate in patientTableRecordsSlice). No abort of
        // the previous fetch: the gate makes it unnecessary, and fetchAccountData coalesces
        // concurrent callers onto the first caller's promise, which is bound to that caller's
        // signal, so aborting it rejects every caller that joined. The controller below exists
        // only because fetchPatientRecordsAsync requires a signal; it is never aborted.
        const controller = new AbortController();
        // Get selected doctor IDs from Redux state
        const state = store.getState() as any;
        const selectedDoctors = state.doctors.selectedDoctors || [];
        const doctorIds = selectedDoctors.length > 0 ? selectedDoctors.map((d: any) => d.id) : null;

        const action = await dispatch(
            fetchPatientRecordsAsync({
                selectedDate: selectedDateRef.current,
                doctorIds,
                abortSignal: controller?.signal,
                userId: clinicAccountId,
            }) as any,
        );

        // Fetch and check for incomplete uploads
        await getIncompleteUploads();

        return !fetchPatientRecordsAsync.rejected.match(action);
    }, [dispatch, getIncompleteUploads, isImpersonating, impersonationSession, session?.user?.id, dataLoaded]);

    // Periodic refresh of patient_record while the calendar/list view is on screen lives in
    // Visit.tsx, not here (PR review, 2026-08-26): DataContextProvider is mounted above the
    // router (see main.tsx), wrapping the whole app for the lifetime of the session -- it has no
    // way to know which page is currently showing, so an interval set up here would run
    // indefinitely on every route, not just while the calendar/list view is actually visible.
    // Visit.tsx is the actual page component for that view; its own mount/unmount lifecycle IS
    // "is this view on screen," which an effect here structurally cannot express. It calls
    // refreshPatientRecords (exposed below) the same way SyncAppointmentsBtn.tsx's manual sync
    // already does.

    const value = {
        selectedDate,
        setSelectedDate,
        isLoadingData,
        setIsLoadingData,
        refreshPatientRecords,
        sectionsAndFieldsToBold,
        setSectionsAndFieldsToBold,
        getIncompleteUploads,
        incompleteUploads,
        setIncompleteUploads,
    };

    return <DataContextApi.Provider value={value as any}>{children}</DataContextApi.Provider>;
};

export const useDataContextApi = () => {
    const context = useContext(DataContextApi);
    if (context === undefined) {
        throw new Error(`DataContextAPi must be used within a DataContextProvider`);
    }
    return context;
};
